import { getStatus } from 'tio-common/helpers/observability';
import type { SlrAlert } from 'tio-data/requests/person';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';

/** A servicer the member can pick, as the question and the catalog both name it. */
export interface ServicerOption {
  /** FinancialInstitution id, always a string — ids exceed JS integer precision. */
  id: string;
  name: string;
}

/**
 * A server-authored alert that carries a pending question the member can answer in
 * place. The member alert host renders one of these by `resolutionType`.
 */
export interface ResolutionAlert {
  type: string;
  resolutionType: SlrAlert['resolution_type'];
  resolutionId: string;
  /** The upload the question was opened on — not necessarily the upload that just reflected. */
  documentId: string;
  persistence: 'persisting' | 'temporary';
  blocksContribution: boolean;
  title: string;
  description: string;
  priority: number;
  /** Ranking hint pinned to the top of the picker; the full list is fetched live. */
  suggestedServicers: ServicerOption[];
}

/** The member-facing status a document question maps to; rendered as "Needs Action". */
export const NEEDS_ACTION_STATUS = 'NEEDS_ACTION';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function asId(value: unknown) {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

// The feed dasherizes metadata keys and the reflection frame camelizes them.
function readMeta(meta: Record<string, unknown>, dasherized: string, camelized: string) {
  return meta[dasherized] ?? meta[camelized];
}

function parseSuggestions(value: unknown): ServicerOption[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    const entry = asRecord(raw);
    const id = asId(entry['id']);
    const name = typeof entry['name'] === 'string' ? entry['name'] : '';
    return id && name ? [{ id, name }] : [];
  });
}

function buildResolutionAlert(fields: {
  type: unknown;
  resolutionType: unknown;
  persistence: unknown;
  blocksContribution: unknown;
  title: unknown;
  description: unknown;
  priority: unknown;
  metadata: unknown;
}): ResolutionAlert | null {
  const meta = asRecord(fields.metadata);
  const resolutionId = asId(readMeta(meta, 'resolution-id', 'resolutionId'));
  const documentId = asId(readMeta(meta, 'document-id', 'documentId'));
  if (!resolutionId || !documentId) return null;

  return {
    type: String(fields.type ?? ''),
    resolutionType: fields.resolutionType as SlrAlert['resolution_type'],
    resolutionId,
    documentId,
    persistence: fields.persistence === 'temporary' ? 'temporary' : 'persisting',
    blocksContribution: fields.blocksContribution !== false,
    title: String(fields.title ?? ''),
    description: String(fields.description ?? ''),
    priority: typeof fields.priority === 'number' ? fields.priority : Number.MAX_SAFE_INTEGER,
    suggestedServicers: parseSuggestions(
      readMeta(meta, 'suggested-servicers', 'suggestedServicers')
    ),
  };
}

/** Null unless the metadata names both the resolution and its document; absent new fields read as persisting + blocking. */
export function toResolutionAlert(alert: SlrAlert): ResolutionAlert | null {
  return buildResolutionAlert({
    type: alert.type,
    resolutionType: alert.resolution_type,
    persistence: alert.persistence,
    blocksContribution: alert.blocks_contribution,
    title: alert.title,
    description: alert.description,
    priority: alert.priority,
    metadata: alert.metadata,
  });
}

/** One `slrAlerts` entry off a reflection broadcast (camelized end to end); null unless it is a pending question. */
export function resolutionAlertFromFrame(raw: unknown): ResolutionAlert | null {
  const alert = asRecord(raw);
  return buildResolutionAlert({
    type: alert['type'],
    resolutionType: alert['resolutionType'],
    persistence: alert['persistence'],
    blocksContribution: alert['blocksContribution'],
    title: alert['title'],
    description: alert['description'],
    priority: alert['priority'],
    metadata: alert['metadata'],
  });
}

/** `question`: the one opened on `documentId`; `otherAlert`: the first non-question. Other uploads' questions are neither. */
export function splitFrameAlerts(frameAlerts: unknown, documentId: string) {
  const alerts = Array.isArray(frameAlerts) ? frameAlerts : [];
  const questions = alerts.map((raw) => resolutionAlertFromFrame(raw));
  const otherIndex = questions.findIndex((question) => question === null);

  return {
    question: questions.find((candidate) => candidate?.documentId === documentId) ?? undefined,
    otherAlert:
      otherIndex === -1 ? undefined : (asRecord(alerts[otherIndex]) as { description?: string }),
  };
}

/** Identity of one question on one document — the unit a member answers or dismisses. */
export function resolutionAlertKey(alert: Pick<ResolutionAlert, 'resolutionId' | 'documentId'>) {
  return `${alert.resolutionId}:${alert.documentId}`;
}

/** Suggestions first, then the catalog A–Z; a loaded catalog drops suggestions it no longer lists. */
export function rankServicerOptions(
  suggested: ServicerOption[],
  catalog: ServicerOption[]
): ServicerOption[] {
  if (!catalog.length) return suggested;

  const catalogIds = new Set(catalog.map((servicer) => servicer.id));
  const pinned = suggested.filter((servicer) => catalogIds.has(servicer.id));
  const pinnedIds = new Set(pinned.map((servicer) => servicer.id));
  const rest = catalog
    .filter((servicer) => !pinnedIds.has(servicer.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...pinned, ...rest];
}

/** Keyed off the member's answer, not reflection: answered reads APPLIED at once; a failed apply still reads failed. */
export function memberDocumentStatus(
  document: ObservableDocumentModel,
  question: { isOpen: boolean; isAnswered: boolean }
) {
  const status = getStatus(document);
  if (status === 'NOT_APPLIED') return status;
  if (question.isAnswered) return 'APPLIED';
  if (status === 'PARTIALLY_APPLIED' && question.isOpen) return NEEDS_ACTION_STATUS;
  return status;
}
